import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { veiculoService } from '../../services/veiculoService';
import { isPessoaAtiva } from '../../services/pessoaService';
import { propostaMetaService } from '../../services/propostaMetaService';
import { propostaSaldoService, PropostaSaldoInfo } from '../../services/propostaSaldoService';
import { OrdemDeServicoFormData } from '../../types/ordemServico';
import { recalculateOS } from './useOrdemServicoCalculations';
import { OsFormInformacoesBasicas } from './OsFormInformacoesBasicas';
import { ClientePedidoSearchSelect, PedidoClienteOption } from './ClientePedidoSearchSelect';
import { OsFormTabVeiculo } from './OsFormTabVeiculo';
import { OsFormTabAjudantes } from './OsFormTabAjudantes';
import { OsFormTabDespesas } from './OsFormTabDespesas';
import { OsFormTotais } from './OsFormTotais';
import { FileText, Save, Eraser, ArrowLeft, X, Loader2, CheckCircle2, AlertTriangle, Truck, Users, Receipt, ShieldAlert } from 'lucide-react';

interface OrdemDeServicoFormProps {
  osId?: number | null;
  initialDate?: string;
  onClose?: () => void;
  onSaved?: (savedId: number) => void;
}

const initialFormState: OrdemDeServicoFormData = {
  pedido_id: '',
  status_id: '',
  data_atendimento: new Date().toISOString().split('T')[0],
  horario_inicio: '',
  horario_final: '',
  hora_total: '',
  retirada: '',
  entrega: '',
  item_servico: '',
  valor_unit_servi: 0,
  item_ajudante: '',
  valor_unit_ajud: 0,
  quantidade_ajudante: 0,
  subtotal_viagem: 0,
  detalhes_adicional: '',
  adicional: 0,
  descricao: '',
  
  tipo_veiculo_id: '',
  veiculos_id: '',
  placa: '',
  veiculo_marca_id: '',
  modelo: '',
  cor: '',
  agregado: '',
  repasse: 0,
  
  ajudantes_list: [],
  ajudante_repasse: 0,
  
  despesas: '',
  valor_despesa: 0,
  
  ocorrencias: '',
  total_os: 0,
  valor_total_repasses: 0,
  valor_liquido: 0,
};

export const OrdemDeServicoForm: React.FC<OrdemDeServicoFormProps> = ({
  osId,
  initialDate,
  onClose,
  onSaved,
}) => {
  const [formData, setFormData] = useState<OrdemDeServicoFormData>(() => ({
    ...initialFormState,
    data_atendimento: initialDate || new Date().toISOString().split('T')[0],
  }));
  const [activeTab, setActiveTab] = useState<'veiculo' | 'ajudantes' | 'despesas'>('veiculo');
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Dropdown options state
  const [pedidosOptions, setPedidosOptions] = useState<PedidoClienteOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<{ id: number; status: string; cor: string }[]>([]);
  const [servicosOptions, setServicosOptions] = useState<{ id: string; servicos: string; valor_unitario: number }[]>([]);
  const [ajudantesOptions, setAjudantesOptions] = useState<{ id: string; servicos: string; valor_unitario: number }[]>([]);
  const [tipoVeiculoOptions, setTipoVeiculoOptions] = useState<{ id: number; tipo_veiculo: string }[]>([]);
  const [veiculosOptions, setVeiculosOptions] = useState<any[]>([]);
  const [pessoasOptions, setPessoasOptions] = useState<{ id: number; nome: string; ativo?: boolean }[]>([]);
  
  // Marcas, Modelos e Cores
  const [marcasOptions, setMarcasOptions] = useState<{ id: number; marca: string }[]>([]);
  const [modelosOptions, setModelosOptions] = useState<{ id: number; modelo: string }[]>([]);
  const [coresOptions, setCoresOptions] = useState<{ id: number; cor: string }[]>([]);
  
  // SEÇÃO: Estados separados para filtros de contrato e pessoas (frotas vs agregados)
  const [agregadosPessoas, setAgregadosPessoas] = useState<{ id: number; nome: string; ativo?: boolean }[]>([]);
  const [ajudantesPessoas, setAjudantesPessoas] = useState<{ id: number; nome: string; ativo?: boolean }[]>([]);

  // SEÇÃO: Controle de Saldo da Proposta e Bloqueio Global
  const [saldoProposta, setSaldoProposta] = useState<PropostaSaldoInfo | null>(null);
  const [loadingSaldo, setLoadingSaldo] = useState(false);
  const [bloqueioSemSaldoAtivo, setBloqueioSemSaldoAtivo] = useState(false);

  useEffect(() => {
    fetchDropdowns();

    // Carrega preferência global de bloqueio de OS sem saldo
    propostaSaldoService.isBloqueioSemSaldoAtivo().then(setBloqueioSemSaldoAtivo);

    // Ouve atualizações de configurações do sistema em tempo real
    const handleConfigChange = (e: any) => {
      if (e?.detail?.ativo !== undefined) {
        setBloqueioSemSaldoAtivo(e.detail.ativo);
      }
    };
    window.addEventListener('config_bloqueio_saldo_changed', handleConfigChange);
    return () => {
      window.removeEventListener('config_bloqueio_saldo_changed', handleConfigChange);
    };
  }, []);

  // SEÇÃO: Efeito para filtrar itens do contrato e calcular saldo baseado no cliente/pedido selecionado
  useEffect(() => {
    const fetchDetalhes = async () => {
      if (formData.pedido_id) {
        setLoading(true);
        setLoadingSaldo(true);
        try {
          // Busca apenas os serviços vinculados ao pedido selecionado
          const { data: detalhesData } = await supabase
            .from('detalhes_do_pedido')
            .select(`*`)
            .eq('pedido_id', formData.pedido_id);

          if (detalhesData) {
            // Separa e filtra registros de metadados da proposta
            const { items: realItems } = propostaMetaService.separateItemsAndMeta(detalhesData);

            const vehicleServices = realItems.filter(d => {
              const tipoStr = (d.servicos || '').toLowerCase();
              return !tipoStr.includes('ajudante') && !tipoStr.startsWith('__meta__');
            });

            const helperServices = realItems.filter(d => {
              const tipoStr = (d.servicos || '').toLowerCase();
              return tipoStr.includes('ajudante') && !tipoStr.startsWith('__meta__');
            });

            setServicosOptions(
              vehicleServices.map((d) => ({
                id: d.id.toString(),
                servicos: d.servicos || `Serviço #${d.id}`,
                valor_unitario: Number(d.valor_unitario || 0),
              }))
            );

            setAjudantesOptions(
              helperServices.map((d) => ({
                id: d.id.toString(),
                servicos: d.servicos || `Ajudante #${d.id}`,
                valor_unitario: Number(d.valor_unitario || 0),
              }))
            );
          }

          // Busca saldo de caminhões e consumo da proposta
          const saldo = await propostaSaldoService.getSaldoProposta(
            Number(formData.pedido_id),
            osId ? Number(osId) : undefined
          );
          setSaldoProposta(saldo);
        } catch (error) {
          console.error("Erro ao buscar detalhes e saldo do pedido:", error);
        } finally {
          setLoading(false);
          setLoadingSaldo(false);
        }
      } else {
        setServicosOptions([]);
        setAjudantesOptions([]);
        setSaldoProposta(null);
      }
    };

    fetchDetalhes();
  }, [formData.pedido_id, osId]);

  useEffect(() => {
    if (osId) {
      loadOSData(osId);
    }
  }, [osId]);

  // Recalcula totais sempre que formData muda nos campos dependentes
  useEffect(() => {
    setFormData((prev) => recalculateOS(prev));
  }, [
    formData.valor_unit_servi,
    formData.valor_unit_ajud,
    formData.quantidade_ajudante,
    formData.adicional,
    formData.repasse,
    formData.valor_despesa,
    JSON.stringify(formData.ajudantes_list),
  ]);

  const fetchDropdowns = async () => {
    setLoading(true);
    try {
      // 1. Pedidos (com dados ricos do cliente para busca instantânea por qualquer campo - apenas clientes ativos)
      const { data: pedidosData } = await supabase
        .from('pedido')
        .select('id, pessoa_id, data_fechamento, pessoa:pessoa_id(id, nome, nome_fantasia, documento, fone, email, ativo, data_desativacao, deleted_at, situacao_cadastral)')
        .order('id', { ascending: false });

      if (pedidosData && pedidosData.length > 0) {
        const activePedidos = pedidosData.filter((p: any) => {
          const rawPessoa = p.pessoa;
          const pessoaObj = Array.isArray(rawPessoa) ? rawPessoa[0] : rawPessoa;
          // Permite se cliente ativo ou se é o pedido atualmente selecionado no formulário
          return isPessoaAtiva(pessoaObj) || String(formData.pedido_id) === String(p.id);
        });

        setPedidosOptions(
          activePedidos.map((p: any) => {
            const rawPessoa = p.pessoa;
            const pessoaObj = Array.isArray(rawPessoa) ? rawPessoa[0] : rawPessoa;
            return {
              id: p.id,
              pessoa_id: p.pessoa_id,
              nome_cliente: pessoaObj?.nome || `Cliente #${p.pessoa_id || 'N/A'}`,
              nome_fantasia: pessoaObj?.nome_fantasia || '',
              documento: pessoaObj?.documento || '',
              fone: pessoaObj?.fone || '',
              email: pessoaObj?.email || '',
            };
          })
        );
      } else {
        // Fallback caso o join falhe
        const { data: simplePedidos } = await supabase.from('pedido').select('id, pessoa_id').order('id', { ascending: false });
        if (simplePedidos && simplePedidos.length > 0) {
          const pessoaIds = simplePedidos.map((p) => p.pessoa_id).filter(Boolean);
          const { data: pessoasData } = await supabase.from('pessoa').select('id, nome, nome_fantasia, documento, fone, email, ativo, data_desativacao, deleted_at, situacao_cadastral').in('id', pessoaIds);
          const pessoaMap = new Map((pessoasData || []).map((p) => [p.id, p]));

          const activeSimplePedidos = simplePedidos.filter((p) => {
            const pData = pessoaMap.get(p.pessoa_id);
            return isPessoaAtiva(pData) || String(formData.pedido_id) === String(p.id);
          });

          setPedidosOptions(
            activeSimplePedidos.map((p) => {
              const pData = pessoaMap.get(p.pessoa_id);
              return {
                id: p.id,
                pessoa_id: p.pessoa_id,
                nome_cliente: pData?.nome || `Cliente #${p.pessoa_id || 'N/A'}`,
                nome_fantasia: pData?.nome_fantasia || '',
                documento: pData?.documento || '',
                fone: pData?.fone || '',
                email: pData?.email || '',
              };
            })
          );
        }
      }

      // 3. Status
      const { data: statusData } = await supabase.from('status').select('id, status, cor').order('id');
      if (statusData) setStatusOptions(statusData);

      // 4. Tipo Veículo
      const { data: tipoVData } = await supabase.from('tipo_veiculo').select('id, tipo_veiculo');
      if (tipoVData) setTipoVeiculoOptions(tipoVData);

      // 4.1 Marcas, Modelos e Cores
      const { data: marcasData } = await supabase.from('veiculo_marca').select('id, marca');
      if (marcasData) setMarcasOptions(marcasData);

      const { data: modelosData } = await supabase.from('veiculo_modelo').select('id, modelo');
      if (modelosData) setModelosOptions(modelosData);

      const { data: coresData } = await supabase.from('veiculo_cor').select('id, cor');
      if (coresData) setCoresOptions(coresData);

      // 5. Pessoas (Agregados/Ajudantes/Motoristas)
      const { data: rawPessoas } = await supabase.from('pessoa').select('*').order('nome');
      const allPessoas = (rawPessoas || []).filter(p => isPessoaAtiva(p));
      
      const agregadoSet = new Set<number>();
      const ajudanteSet = new Set<number>();
      const motoristaSet = new Set<number>();

      if (allPessoas) {
        setPessoasOptions(allPessoas);
        
        try {
          // SEÇÃO: Busca as associações de grupo de pessoas para dividir agregados e ajudantes
          const { data: associations } = await supabase
            .from('pessoa_grupo')
            .select('pessoa_id, grupo_pessoa(nome)');
            
          associations?.forEach((assoc: any) => {
            const groupName = assoc.grupo_pessoa?.nome?.toLowerCase() || '';
            if (groupName.includes('agregado')) {
              agregadoSet.add(assoc.pessoa_id);
            }
            if (groupName.includes('ajudante')) {
              ajudanteSet.add(assoc.pessoa_id);
            }
            if (groupName.includes('motorista')) {
              motoristaSet.add(assoc.pessoa_id);
            }
          });
          
          const filteredAgregados = allPessoas.filter(p => agregadoSet.has(p.id));
          const filteredAjudantes = allPessoas.filter(p => ajudanteSet.has(p.id));
          
          setAgregadosPessoas(filteredAgregados.length > 0 ? filteredAgregados : allPessoas);
          setAjudantesPessoas(filteredAjudantes.length > 0 ? filteredAjudantes : allPessoas);
        } catch (groupErr) {
          console.error('Erro ao separar grupos de pessoas, usando fallbacks:', groupErr);
          setAgregadosPessoas(allPessoas);
          setAjudantesPessoas(allPessoas);
        }
      }

      // 6. Veículos & Motoristas
      const vData = await veiculoService.getAll();
      if (vData) {
        const pessoaMap = new Map((allPessoas || []).map((m) => [m.id, m.nome]));

        const veiculosMapped = vData.map((v) => {
          const mName = pessoaMap.get(v.motorista_id) || pessoaMap.get(v.agregado_id) || '';
          return {
            ...v,
            motorista_nome: mName,
          };
        });

        // Add standalone drivers from pessoa who don't have a vehicle row in veiculos table
        const linkedPessoaIds = new Set(vData.flatMap((v) => [v.motorista_id, v.agregado_id]).filter(Boolean));
        const excludeKeywords = ['teste', 'vago', 'vazio', 'provisorio', 'mock', 'cadastro', 'estacionamento', 'posto'];
        const standaloneDrivers: any[] = (allPessoas || [])
          .filter((p) => {
            const isStandAlone = p.ativo !== false && !linkedPessoaIds.has(p.id);
            const isDriverOrAgregado = motoristaSet.has(p.id) || agregadoSet.has(p.id);
            const nomeLower = (p.nome || '').toLowerCase();
            const isInvalidName = excludeKeywords.some((keyword) => nomeLower.includes(keyword));
            return isStandAlone && isDriverOrAgregado && !isInvalidName;
          })
          .map((p) => {
            const isAgregado = agregadoSet.has(p.id);
            const isMotorista = motoristaSet.has(p.id);
            return {
              id: `p_${p.id}`,
              placa: 'Sem Veículo',
              motorista_id: isMotorista ? p.id : null,
              motorista_nome: p.nome,
              agregado_id: isAgregado ? p.id : null,
              isStandaloneDriver: true,
            };
          });

        setVeiculosOptions([...veiculosMapped, ...standaloneDrivers]);
      }
    } catch (err) {
      console.error('Erro ao carregar opções:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOSData = async (id: number) => {
    setLoading(true);
    try {
      const { data: osData, error } = await supabase
        .from('ordem_de_servico')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      // Busca ajudantes vinculados na tabela ajudantes_os
      const { data: ajudantesData } = await supabase
        .from('ajudantes_os')
        .select('*')
        .eq('ordem_de_servico_id', id);

      const ajudantesList = (ajudantesData || []).map((a) => ({
        id: a.id,
        ajudantes_id: a.ajudantes_id || '',
        repasse_ajudante: Number(a.repasse_ajudante || 0),
      }));

      setFormData({
        id: osData.id,
        pedido_id: osData.pedido_id || '',
        status_id: osData.status_id || '',
        data_atendimento: osData.data_atendimento || '',
        horario_inicio: osData.horario_inicio || '',
        horario_final: osData.horario_final || '',
        hora_total: osData.hora_total || '',
        retirada: osData.retirada || '',
        entrega: osData.entrega || '',
        item_servico: osData.item_servico || '',
        valor_unit_servi: Number(osData.valor_unit_servi || 0),
        item_ajudante: osData.item_ajudante || '',
        valor_unit_ajud: Number(osData.valor_unit_ajud || 0),
        quantidade_ajudante: Number(osData.quantidade_ajudante || 0),
        subtotal_viagem: Number(osData.subtotal_viagem || 0),
        detalhes_adicional: osData.detalhes_adicional || '',
        adicional: Number(osData.adicional || 0),
        descricao: osData.descricao || '',
        
        tipo_veiculo_id: osData.tipo_veiculo_id || '',
        veiculos_id: osData.veiculos_id || '',
        placa: osData.placa || '',
        veiculo_marca_id: osData.veiculo_marca_id || '',
        modelo: osData.modelo || '',
        cor: osData.cor || '',
        agregado: osData.agregado || '',
        repasse: Number(osData.repasse || 0),
        
        ajudantes_list: ajudantesList,
        ajudante_repasse: Number(osData.ajudante_repasse || 0),
        
        despesas: osData.despesas || '',
        valor_despesa: Number(osData.valor_despesa || 0),
        
        ocorrencias: osData.ocorrencias || '',
        total_os: Number(osData.total_os || 0),
        valor_total_repasses: Number(osData.valor_total_repasses || 0),
        valor_liquido: Number(osData.valor_liquido || 0),
      });
    } catch (err: any) {
      console.error('Erro ao carregar OS para edição:', err);
      setErrorMsg('Falha ao carregar os dados da ordem de serviço.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData(initialFormState);
    setErrorMsg(null);
    setSuccessMsg(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      // Validação de Saldo da Proposta por Item Específico (se for criação de nova OS)
      if (!formData.id && formData.pedido_id) {
        const currentSaldo = await propostaSaldoService.getSaldoProposta(Number(formData.pedido_id));
        const bloqueioConfig = await propostaSaldoService.isBloqueioSemSaldoAtivo();

        if (bloqueioConfig && currentSaldo) {
          const validacao = propostaSaldoService.validarSaldoEmissaoOs(
            currentSaldo,
            formData.item_servico,
            formData.item_ajudante,
            formData.quantidade_ajudante
          );

          if (!validacao.permitido) {
            setErrorMsg(
              `Operação Bloqueada: ${validacao.motivoBloqueio} Conforme as configurações do sistema, a emissão de Ordens de Serviço está bloqueada para itens sem saldo.`
            );
            setSaving(false);
            return;
          }
        }
      }

      const payload: any = {
        pedido_id: formData.pedido_id ? Number(formData.pedido_id) : null,
        status_id: formData.status_id ? Number(formData.status_id) : null,
        data_atendimento: formData.data_atendimento || null,
        horario_inicio: formData.horario_inicio || null,
        horario_final: formData.horario_final || null,
        hora_total: formData.hora_total || null,
        retirada: formData.retirada || null,
        entrega: formData.entrega || null,
        item_servico: formData.item_servico || null,
        valor_unit_servi: formData.valor_unit_servi,
        item_ajudante: formData.item_ajudante || null,
        valor_unit_ajud: formData.valor_unit_ajud,
        quantidade_ajudante: formData.quantidade_ajudante,
        subtotal_viagem: formData.subtotal_viagem,
        detalhes_adicional: formData.detalhes_adicional || '',
        adicional: formData.adicional,
        descricao: formData.descricao || null,
        
        tipo_veiculo_id: formData.tipo_veiculo_id ? Number(formData.tipo_veiculo_id) : null,
        veiculos_id: formData.veiculos_id || null,
        // placa foi removido do payload porque na tabela ordem_de_servico ele está como INT
        // e a placa é um texto (ex: FAJ6A89), causando erro 'invalid input syntax for type integer'
        veiculo_marca_id: formData.veiculo_marca_id ? Number(formData.veiculo_marca_id) : null,
        modelo: formData.modelo ? Number(formData.modelo) : null,
        cor: formData.cor ? Number(formData.cor) : null,
        agregado: formData.agregado ? Number(formData.agregado) : null,
        repasse: formData.repasse,
        
        ajudante_repasse: formData.ajudante_repasse,
        despesas: formData.despesas || null,
        valor_despesa: formData.valor_despesa,
        
        ocorrencias: formData.ocorrencias || null,
        total_os: formData.total_os,
        valor_total_repasses: formData.valor_total_repasses,
        valor_liquido: formData.valor_liquido,
        updated_at: new Date().toISOString(),
      };

      let currentOsId = formData.id;

      if (currentOsId) {
        // Atualiza OS existente
        const { error: updateError } = await supabase
          .from('ordem_de_servico')
          .update(payload)
          .eq('id', currentOsId);

        if (updateError) throw updateError;
      } else {
        // Cria nova OS
        const { data: newOs, error: insertError } = await supabase
          .from('ordem_de_servico')
          .insert([payload])
          .select('id')
          .single();

        if (insertError) throw insertError;
        currentOsId = newOs.id;
      }

      // Salva a lista de Ajudantes na tabela ajudantes_os
      let ajudantesWarning: string | null = null;
      if (currentOsId) {
        // Limpa ajudantes antigos
        const { error: delAjudantesErr } = await supabase
          .from('ajudantes_os')
          .delete()
          .eq('ordem_de_servico_id', currentOsId);

        if (delAjudantesErr) {
          console.error('Erro ao limpar ajudantes antigos:', delAjudantesErr);
          ajudantesWarning = 'A OS foi salva, mas houve falha ao atualizar a lista de ajudantes/repasses. Verifique antes de faturar.';
        } else if (formData.ajudantes_list.length > 0) {
          const ajudantsToInsert = formData.ajudantes_list
            .filter((a) => a.ajudantes_id)
            .map((a) => ({
              ordem_de_servico_id: currentOsId,
              ajudantes_id: Number(a.ajudantes_id),
              repasse_ajudante: Number(a.repasse_ajudante || 0),
            }));

          if (ajudantsToInsert.length > 0) {
            const { error: insAjudantesErr } = await supabase.from('ajudantes_os').insert(ajudantsToInsert);
            if (insAjudantesErr) {
              console.error('Erro ao inserir ajudantes:', insAjudantesErr);
              ajudantesWarning = 'A OS foi salva, mas houve falha ao gravar os ajudantes/repasses. Verifique antes de faturar.';
            }
          }
        }
      }

      setSuccessMsg(
        ajudantesWarning
          ? `Ordem de serviço #${currentOsId} salva, com um alerta: ${ajudantesWarning}`
          : `Ordem de serviço #${currentOsId} salva com sucesso!`
      );
      if (onSaved && currentOsId) {
        onSaved(currentOsId);
      }
    } catch (err: any) {
      console.error('Erro ao salvar Ordem de Serviço:', err);
      setErrorMsg(err.message || 'Ocorreu um erro ao salvar a ordem de serviço.');
    } finally {
      setSaving(false);
    }
  };

  const handleMotoristaAtualizado = (veiculoId: string, motoristaId: number, motoristaNome: string, newPessoaObj?: any) => {
    setVeiculosOptions((prev) =>
      prev.map((v) => {
        if (String(v.id) === String(veiculoId)) {
          return {
            ...v,
            motorista_id: motoristaId,
            motorista_nome: motoristaNome,
          };
        }
        return v;
      })
    );

    if (newPessoaObj) {
      setPessoasOptions((prev) => [...prev, newPessoaObj]);
      setAgregadosPessoas((prev) => [...prev, newPessoaObj]);
    }

    const foundVeiculo = veiculosOptions.find((v) => String(v.id) === String(veiculoId));
    setFormData((prev) => ({
      ...prev,
      veiculos_id: String(veiculoId),
      placa: foundVeiculo?.placa || prev.placa,
      veiculo_marca_id: foundVeiculo?.veiculo_marca_id ? String(foundVeiculo.veiculo_marca_id) : prev.veiculo_marca_id,
      modelo: foundVeiculo?.veiculo_modelo_id ? String(foundVeiculo.veiculo_modelo_id) : prev.modelo,
      cor: foundVeiculo?.veiculo_cor_id ? String(foundVeiculo.veiculo_cor_id) : prev.cor,
      tipo_veiculo_id: foundVeiculo?.tipo_veiculo ? String(foundVeiculo.tipo_veiculo) : prev.tipo_veiculo_id,
      agregado: motoristaId,
    }));

    setSuccessMsg(`Motorista "${motoristaNome}" vinculado ao veículo com sucesso!`);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-4 sm:p-6 shadow-2xl max-w-4xl w-full mx-auto my-auto max-h-[90vh] flex flex-col overflow-y-auto space-y-6">
      {/* Cabeçalho do Formulário */}
      <div className="sticky top-0 bg-white z-20 -mx-4 -mt-4 sm:-mx-6 sm:-mt-6 px-4 pt-4 sm:px-6 sm:pt-5 pb-4 border-b border-slate-200 flex items-center justify-between shadow-xs">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-50 text-red-600 rounded-xl">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {formData.id ? `Editar Ordem de Serviço #${formData.id}` : 'Cadastro de Ordem de Serviço'}
            </h2>
            <p className="text-xs text-slate-500">
              Preencha os campos para salvar a Ordem de Serviço no sistema
            </p>
          </div>
        </div>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
            title="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Mensagens de Sucesso ou Erro */}
      {successMsg && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-bold flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="p-3.5 bg-red-50 border border-red-200 text-red-800 rounded-xl text-xs font-bold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center text-slate-500 gap-3">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
          <span className="text-xs font-medium">Carregando formulário e opções...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Seção 1: Informações Básicas (Cliente, Atendimento, Serviço, Ajudantes) */}
          <OsFormInformacoesBasicas
            formData={formData}
            setFormData={setFormData}
            pedidosOptions={pedidosOptions}
            statusOptions={statusOptions}
            servicosOptions={servicosOptions}
            ajudantesOptions={ajudantesOptions}
            loadingDropdowns={loading}
            saldoProposta={saldoProposta}
            loadingSaldo={loadingSaldo}
            bloqueioSemSaldoAtivo={bloqueioSemSaldoAtivo}
          />

          {/* Seção 2: Abas (Veículo, Ajudantes, Despesas) */}
          <div className="space-y-3">
            <div className="flex border-b border-slate-200 gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('veiculo')}
                className={`px-4 py-2 text-xs font-bold rounded-t-lg transition-colors flex items-center gap-1.5 border-b-2 ${
                  activeTab === 'veiculo'
                    ? 'border-red-600 text-red-600 bg-red-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                }`}
              >
                <Truck className="w-3.5 h-3.5" />
                Veículo
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('ajudantes')}
                className={`px-4 py-2 text-xs font-bold rounded-t-lg transition-colors flex items-center gap-1.5 border-b-2 ${
                  activeTab === 'ajudantes'
                    ? 'border-red-600 text-red-600 bg-red-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                Ajudantes ({formData.ajudantes_list.length})
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('despesas')}
                className={`px-4 py-2 text-xs font-bold rounded-t-lg transition-colors flex items-center gap-1.5 border-b-2 ${
                  activeTab === 'despesas'
                    ? 'border-red-600 text-red-600 bg-red-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                }`}
              >
                <Receipt className="w-3.5 h-3.5" />
                Despesas Extras
              </button>
            </div>

            {/* Conteúdo da Aba Ativa */}
            {activeTab === 'veiculo' && (
              <OsFormTabVeiculo
                formData={formData}
                setFormData={setFormData}
                tipoVeiculoOptions={tipoVeiculoOptions}
                veiculosOptions={veiculosOptions.filter(v => (v.ativo !== false && v.status_frota !== 'em_manutencao' && v.status_frota !== 'desativado' && (!v.motorista_id || pessoasOptions.find(p => p.id === v.motorista_id)?.ativo !== false)) || String(v.id) === String(formData.veiculos_id))}
                pessoasOptions={agregadosPessoas.filter(p => p.ativo !== false || String(p.id) === String(formData.agregado))}
                marcasOptions={marcasOptions}
                modelosOptions={modelosOptions}
                coresOptions={coresOptions}
                onMotoristaAtualizado={handleMotoristaAtualizado}
              />
            )}

            {activeTab === 'ajudantes' && (
              <OsFormTabAjudantes
                formData={formData}
                setFormData={setFormData}
                pessoasOptions={ajudantesPessoas.filter(p => p.ativo !== false || formData.ajudantes_list?.some(a => String(a.ajudantes_id) === String(p.id)))}
              />
            )}

            {activeTab === 'despesas' && (
              <OsFormTabDespesas
                formData={formData}
                setFormData={setFormData}
              />
            )}
          </div>

          {/* Seção 3: Totais & Fechamento */}
          <OsFormTotais
            formData={formData}
            setFormData={setFormData}
          />
        </div>
      )}

      {/* Botões de Ação */}
      <div className="sticky bottom-0 bg-white z-20 -mx-4 -mb-4 sm:-mx-6 sm:-mb-6 p-4 sm:px-6 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 shadow-xs">
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Voltar
            </button>
          )}

          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5"
          >
            <Eraser className="w-3.5 h-3.5 text-slate-500" />
            Limpar Form
          </button>
        </div>

        <div className="flex items-center gap-3">
          {!formData.id && bloqueioSemSaldoAtivo && saldoProposta && !saldoProposta.temSaldo && saldoProposta.statusSaldo === 'esgotado' && (
            <span className="text-2xs font-semibold text-red-700 bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-2xs">
              <ShieldAlert className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
              Bloqueado: Saldo Esgotado ({saldoProposta.totalUtilizado}/{saldoProposta.totalContratado})
            </span>
          )}

          <button
            type="submit"
            disabled={saving || (!formData.id && bloqueioSemSaldoAtivo && saldoProposta?.statusSaldo === 'esgotado')}
            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-colors shadow-md flex items-center gap-2 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Salvar O.S
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};
